import {useCallback, useEffect, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import {SaitamaAd, useAdvertising} from '@adv-mt/advertising-publift'
import AdDisplay from '@adv-mt/mt-ad-display'
import LeadPhoneCall, {leadPhoneCallColors, leadPhoneCallSizes, leadPhoneCallTarget} from '@adv-mt/mt-lead-phone-call'
import IconChevronLeft from '@adv-mt/mt-svg-icons/lib/ChevronLeft'
import IconChevronRight from '@adv-mt/mt-svg-icons/lib/ChevronRight'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import {BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {
  Dialog,
  DIALOG_SIZES,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogPopup
} from '@adv-mt/ui/dialog'
import {LOADING, OBJECT_FIT} from '@adv-mt/ui/media-image'

import AdActions from '../../components/secondaryBar/AdActions.js'
import {AD_ACTIONS_VARIANTS} from '../../components/secondaryBar/config.js'
import {ADJACENT_RADIUS, mediaItemShape, MODAL_BASE_CLASS, WHEEL_COOLDOWN_MS} from './config.js'
import GalleryControls from './GalleryControls.js'
import GalleryIconButton from './GalleryIconButton.js'
import GalleryMediaItem from './GalleryMediaItem.js'
import {GALLERY_TAG_TYPES} from './GalleryTag.js'
import {getMediaItemKey} from './helpers.js'
import useCarouselIndex from './useCarouselIndex.js'
import useLegacyModalEscapeGuard from './useLegacyModalEscapeGuard.js'

const GalleryModal = ({
  isOpen,
  startIndex,
  mediaItems,
  alt,
  hasVideo,
  videoIndex,
  shouldAutoPlayVideo,
  ad,
  adType,
  breadcrumb,
  contractId,
  extendedTrackingData,
  hasDiscardButton,
  isFavorite,
  isProfessionalDealer,
  phone,
  hidePhone,
  seller,
  onClose,
  onVideoPlayingChange,
  showGalleryAd
}) => {
  const {domain, i18n} = useSuiContext()
  const {PubPhoto, ADS_LIST_POSITIONS} = domain.get('config').get('advertising')
  const {ADS_DETAIL_GALLERY_PAGE_NAME} = domain.get('config').get('page')
  const {currentIndex, setCurrentIndex, handleScroll} = useCarouselIndex(startIndex ?? 0)
  const scrollRef = useRef(null)

  // AdDisplay replaces the whole advertising configuration with the gallery page's a moment after the
  // modal opens. A SaitamaAd mounted before that displays against the old configuration and then again
  // against the new one, and the second request cancels the first, so the slot stays empty.
  const {advertisingPageData} = useAdvertising()
  const [pageDataOnOpen, setPageDataOnOpen] = useState(null)
  useEffect(() => {
    setPageDataOnOpen(isOpen ? {value: advertisingPageData} : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])
  const isGalleryAdConfigReady = pageDataOnOpen !== null && advertisingPageData !== pageDataOnOpen.value

  const totalCount = mediaItems.length
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < totalCount - 1

  const scrollToIndex = useCallback((index, instant = false) => {
    const el = scrollRef.current
    if (!el) return
    if (instant) {
      el.scrollLeft = index * el.clientWidth
    } else {
      el.scrollTo({left: index * el.clientWidth, behavior: 'smooth'})
    }
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const idx = startIndex ?? 0
    setCurrentIndex(idx)
    requestAnimationFrame(() => scrollToIndex(idx, true))
  }, [isOpen, startIndex, scrollToIndex, setCurrentIndex])

  const navStateRef = useRef({currentIndex, totalCount})
  navStateRef.current = {currentIndex, totalCount}

  const goToOffset = useCallback(
    offset => {
      const {currentIndex: index, totalCount: count} = navStateRef.current
      scrollToIndex(Math.min(count - 1, Math.max(0, index + offset)))
    },
    [scrollToIndex]
  )

  const goToPrev = useCallback(() => goToOffset(-1), [goToOffset])
  const goToNext = useCallback(() => goToOffset(1), [goToOffset])
  const goToVideo = useCallback(() => scrollToIndex(videoIndex), [videoIndex, scrollToIndex])
  const handleOpenChange = useLegacyModalEscapeGuard({isOpen, onClose})

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = e => {
      if (e.key === 'ArrowLeft') goToPrev()
      else if (e.key === 'ArrowRight') goToNext()
    }
    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, goToPrev, goToNext])

  useEffect(() => {
    if (!isOpen) return
    const el = scrollRef.current
    if (!el) return
    let cooldownId = null
    const handleWheel = e => {
      e.preventDefault()
      if (cooldownId !== null) return
      const delta = Math.abs(e.deltaX) >= Math.abs(e.deltaY) ? e.deltaX : e.deltaY
      if (delta === 0) return
      cooldownId = setTimeout(() => {
        cooldownId = null
      }, WHEEL_COOLDOWN_MS)
      goToOffset(Math.sign(delta))
    }
    el.addEventListener('wheel', handleWheel, {passive: false})
    return () => {
      el.removeEventListener('wheel', handleWheel)
      if (cooldownId !== null) clearTimeout(cooldownId)
    }
  }, [isOpen, goToOffset])

  const closeLabel = i18n.t('COMMON_CLOSE')
  const prevLabel = i18n.t('COMMON.PREVIOUS')
  const nextLabel = i18n.t('COMMON.NEXT')
  const videoLabel = i18n.t('COMMON.VIDEO')
  const galleryLabel = i18n.t('COMMON.GALLERY.FULLSCREEN')
  const counterLabel = `${currentIndex + 1}/${totalCount}`

  const controlsProps = {
    counterType: GALLERY_TAG_TYPES.IMAGE,
    counterLabel,
    hasVideo,
    videoLabel,
    onVideoClick: goToVideo,
    negative: true
  }

  return (
    <>
      {isOpen && <AdDisplay pageName={ADS_DETAIL_GALLERY_PAGE_NAME} ad={ad} adType={adType} seller={seller} />}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogPopup size={DIALOG_SIZES.FULL} aria-label={galleryLabel}>
          <DialogContent className={MODAL_BASE_CLASS}>
            <div className={`${MODAL_BASE_CLASS}-header`}>
              <DialogClose asChild>
                <GalleryIconButton icon={IconClose} variant={BUTTON_VARIANTS.FLAT} aria-label={closeLabel} />
              </DialogClose>

              <GalleryControls className={`${MODAL_BASE_CLASS}-header-controls`} {...controlsProps} />

              <div className={`${MODAL_BASE_CLASS}-header-right`}>
                <AdActions
                  ad={ad}
                  breadcrumb={breadcrumb}
                  contractId={contractId}
                  extendedTrackingData={extendedTrackingData}
                  hasDiscardButton={hasDiscardButton}
                  isFavorite={isFavorite}
                  isNegative
                  variant={AD_ACTIONS_VARIANTS.GALLERY}
                />
                {!hidePhone && (
                  <div className={`${MODAL_BASE_CLASS}-header-phone`}>
                    <LeadPhoneCall
                      adId={ad.id}
                      contractId={contractId}
                      isProfessionalDealer={isProfessionalDealer}
                      phone={phone || ''}
                      color={leadPhoneCallColors.ACCENT}
                      showIcon
                      size={leadPhoneCallSizes.medium}
                      target={leadPhoneCallTarget.adDetailImageGallery}
                      trackHitOrigin
                    />
                  </div>
                )}
              </div>
            </div>

            <DialogBody className={`${MODAL_BASE_CLASS}-body`}>
              <span className={`${MODAL_BASE_CLASS}-arrow`}>
                <GalleryIconButton
                  icon={IconChevronLeft}
                  variant={BUTTON_VARIANTS.OUTLINE}
                  onClick={goToPrev}
                  disabled={!hasPrev}
                  aria-label={prevLabel}
                />
              </span>

              <div ref={scrollRef} className={`${MODAL_BASE_CLASS}-track`} onScroll={handleScroll} tabIndex={-1}>
                {mediaItems.map((item, i) => (
                  <div
                    key={getMediaItemKey(item, i)}
                    className={`${MODAL_BASE_CLASS}-slide`}
                    aria-hidden={Math.abs(i - currentIndex) > ADJACENT_RADIUS || undefined}
                  >
                    {Math.abs(i - currentIndex) <= ADJACENT_RADIUS && (
                      <GalleryMediaItem
                        item={item}
                        alt={i18n.t('COMMON.GALLERY.PHOTO_ALT', {title: alt, index: i + 1, total: totalCount})}
                        index={i}
                        className={`${MODAL_BASE_CLASS}-media`}
                        objectFit={OBJECT_FIT.CONTAIN}
                        loading={LOADING.EAGER}
                        isInteractive={false}
                        isActive={i === currentIndex}
                        hasVideoControls
                        shouldAutoPlayVideo={shouldAutoPlayVideo}
                        onVideoPlayingChange={onVideoPlayingChange}
                      />
                    )}
                  </div>
                ))}
              </div>

              <span className={`${MODAL_BASE_CLASS}-arrow`}>
                <GalleryIconButton
                  icon={IconChevronRight}
                  variant={BUTTON_VARIANTS.OUTLINE}
                  onClick={goToNext}
                  disabled={!hasNext}
                  aria-label={nextLabel}
                />
              </span>

              <GalleryControls className={`${MODAL_BASE_CLASS}-controls`} {...controlsProps} />
            </DialogBody>

            {showGalleryAd && (
              <div className={`${MODAL_BASE_CLASS}-advertisingWrapper`}>
                {isGalleryAdConfigReady && <SaitamaAd id={ADS_LIST_POSITIONS[PubPhoto].id} />}
              </div>
            )}

            {!hidePhone && (
              <DialogFooter className={`${MODAL_BASE_CLASS}-footer`}>
                <LeadPhoneCall
                  adId={ad.id}
                  contractId={contractId}
                  isProfessionalDealer={isProfessionalDealer}
                  isFullWidth
                  phone={phone || ''}
                  showIcon
                  size={leadPhoneCallSizes.large}
                  target={leadPhoneCallTarget.adDetailImageGallery}
                  trackHitOrigin
                />
              </DialogFooter>
            )}
          </DialogContent>
        </DialogPopup>
      </Dialog>
    </>
  )
}

GalleryModal.displayName = 'GalleryModal'

GalleryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  startIndex: PropTypes.number,
  mediaItems: PropTypes.arrayOf(mediaItemShape).isRequired,
  alt: PropTypes.string,
  hasVideo: PropTypes.bool,
  videoIndex: PropTypes.number,
  shouldAutoPlayVideo: PropTypes.bool,
  ad: PropTypes.object.isRequired,
  adType: PropTypes.object,
  breadcrumb: PropTypes.arrayOf(PropTypes.shape({url: PropTypes.string})),
  contractId: PropTypes.string,
  extendedTrackingData: PropTypes.object,
  hasDiscardButton: PropTypes.bool,
  hidePhone: PropTypes.bool,
  isFavorite: PropTypes.bool,
  isProfessionalDealer: PropTypes.bool,
  phone: PropTypes.string,
  seller: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onVideoPlayingChange: PropTypes.func,
  showGalleryAd: PropTypes.bool
}

export default GalleryModal
